// NON-PRODUCTION TEST OVERRIDE — will be removed once testing for trips is done
//
// Lets testers force experiment variants without toggling flags in the
// experimentation tool. Overrides are stored in sessionStorage so they are
// scoped to the browser tab — two tabs can run different variants at once
// (e.g. one `available`, one `control`). An override persists for the life of
// the tab (survives in-tab navigations and reloads, cleared when the tab
// closes).
//
// Set/clear via a query param (write-through to sessionStorage), e.g.:
//   ?experimentOverride=corp-mytrips-replatforming:available
//   ?experimentOverride=corp-mytrips-replatforming:control
//   ?experimentOverride=corp-mytrips-replatforming:clear   (removes override)
// After the first load with the param, the value persists from sessionStorage
// for that tab — the param is no longer needed on subsequent URLs. Multiple
// flags can be overridden; each is stored independently. You can also
// edit/clear them directly in devtools under the Session Storage key
// "experimentOverrides".
//
// Gated to non-production environments (local dev + deployed staging): locally
// the served env sets ENV="localDevelopment", deployed staging sets
// ENV="staging"; production sets ENV="production" and is never overridden.
const OVERRIDE_STORAGE_KEY = "experimentOverrides";
const OVERRIDE_QUERY_PARAM = "experimentOverride";
const CLEAR_VALUE = "clear";

const isNonProd = (): boolean =>
  typeof window !== "undefined" && window.__mclean_env__?.ENV !== "production";

const readStore = (): Record<string, string> => {
  try {
    const raw = window.sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store: Record<string, string>): void => {
  try {
    window.sessionStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore write failures (storage disabled / full)
  }
};

// Only re-parse + persist the query param when the URL actually changes, so
// repeated flag reads during a render don't spam sessionStorage writes.
let lastSyncedSearch: string | null = null;

const syncQueryParamToStore = (): void => {
  const { search } = window.location;
  if (search === lastSyncedSearch) {
    return;
  }
  lastSyncedSearch = search;

  const param = new URLSearchParams(search).get(OVERRIDE_QUERY_PARAM);
  if (!param) {
    return;
  }

  const [experimentId, variant] = param.split(":");
  if (!experimentId) {
    return;
  }

  const store = readStore();
  if (!variant || variant === CLEAR_VALUE) {
    delete store[experimentId];
  } else {
    store[experimentId] = variant;
  }
  writeStore(store);
};

export const getExperimentOverride = (experimentId: string): string | null => {
  if (!isNonProd()) {
    return null;
  }

  syncQueryParamToStore();
  return readStore()[experimentId] ?? null;
};
